import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from "react";

interface CustomerUser {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  address?: string;
  city?: string;
  country?: string;
  zipCode?: string;
}

interface AdminUser {
  id: number;
  username: string;
  fullName: string;
  email: string;
  phone?: string | null;
  role: string;
  mustChangePassword?: boolean;
}

interface AuthContextType {
  customer: CustomerUser | null;
  admin: AdminUser | null;
  customerLoading: boolean;
  adminLoading: boolean;
  loginCustomer: (email: string, password: string) => Promise<void>;
  registerCustomer: (data: { firstName: string; lastName: string; email: string; password: string; phone: string }) => Promise<void>;
  logoutCustomer: () => Promise<void>;
  loginAdmin: (identifier: string, password: string) => Promise<void>;
  logoutAdmin: () => Promise<void>;
  refreshCustomer: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

const API_BASE = import.meta.env.BASE_URL.replace(/\/$/, "") + "/api";

async function apiFetch(path: string, options?: RequestInit) {
  let res: Response;
  try {
    // The platform-admin "impersonate" tenant slug is injected as
    // `X-Tenant-Slug` automatically by the global fetch interceptor
    // installed in main.tsx (see `lib/impersonate-tenant-header.ts`).
    res = await fetch(`${API_BASE}${path}`, {
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      ...options,
    });
  } catch (e) {
    // Network-level failure (TypeError: Failed to fetch). Could be: offline,
    // service-worker glitch, server timeout / 502 from edge proxy. Surface
    // an actionable bilingual message instead of the raw English TypeError.
    const isAr = typeof document !== "undefined" && document.documentElement.lang === "ar";
    throw new Error(isAr
      ? "تعذّر الاتصال بالخادم. تحقّق من الإنترنت ثم أعِد المحاولة. إن استمر الخطأ اضغط Ctrl+Shift+R لتحديث الصفحة بالكامل."
      : "Could not reach the server. Check your connection and retry. If this persists, press Ctrl+Shift+R to hard-refresh.");
  }
  let data: any = null;
  try { data = await res.json(); } catch { data = null; }
  if (!res.ok) {
    const isAr = typeof document !== "undefined" && document.documentElement.lang === "ar";
    const fallback = isAr ? "فشل الطلب" : "Request failed";
    throw new Error((data && (data.error || data.message)) || fallback);
  }
  return data;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [customer, setCustomer] = useState<CustomerUser | null>(null);
  const [admin, setAdmin] = useState<AdminUser | null>(null);
  const [customerLoading, setCustomerLoading] = useState(true);
  const [adminLoading, setAdminLoading] = useState(true);

  const tryAutoLinkPlatform = useCallback(async (): Promise<CustomerUser | null> => {
    try {
      // Check if a platform login session exists on this tenant domain.
      // The endpoint is a soft-probe: it returns 200 + `{ authenticated: false }`
      // for anonymous visitors so the browser console stays clean. We must
      // therefore treat that sentinel as a miss (otherwise we'd try to
      // consent-link a non-existent session).
      const probe = await apiFetch("/platform-auth/me");
      if (!probe || probe.authenticated === false) return null;
    } catch {
      return null;
    }
    try {
      // Auto-create tenant customer + copy addresses + set tenant session.
      await apiFetch("/platform-auth/consent-link", { method: "POST" });
    } catch {
      return null;
    }
    try {
      const linked = await apiFetch("/auth/customer/me");
      if (!linked || linked.authenticated === false) return null;
      return linked;
    } catch {
      return null;
    }
  }, []);

  const refreshCustomer = useCallback(async () => {
    try {
      const data = await apiFetch("/auth/customer/me");
      if (data && data.authenticated !== false) {
        setCustomer(data);
        return;
      }
    } catch {
      // fall through to platform auto-link
    }
    const linked = await tryAutoLinkPlatform();
    setCustomer(linked);
  }, [tryAutoLinkPlatform]);

  useEffect(() => {
    let cancelled = false;

    // The express-session cookie (`connect.sid`) is httpOnly:true, so JS can
    // NEVER see it via document.cookie. Any pre-flight optimisation that
    // tries to use document.cookie to "skip /me for guests" silently breaks
    // OAuth: after a successful Google/Apple/Facebook redirect the session
    // cookie IS set on the tenant origin, but the bootstrap would still
    // skip /me, leaving the user stuck behind the login modal forever
    // (production audit, May 2026 — luxurygive.com checkout). We always
    // run /me on the storefront so freshly-set sessions are detected.
    const path = (typeof window !== "undefined" ? window.location.pathname : "") || "";
    const isAdminLike = /^\/(admin|platform-admin|platform)(\/|$)/.test(path);

    const runAuthBoot = () => {
      (async () => {
        try {
          const data = await apiFetch("/auth/customer/me");
          if (data && data.authenticated !== false) {
            if (!cancelled) setCustomer(data);
          } else {
            const linked = await tryAutoLinkPlatform();
            if (!cancelled) setCustomer(linked);
          }
        } catch {
          const linked = await tryAutoLinkPlatform();
          if (!cancelled) setCustomer(linked);
        } finally {
          if (!cancelled) setCustomerLoading(false);
        }
      })();

      // /auth/admin/me is only relevant inside /admin or /platform-admin
      // shells. Guests on the storefront skip the round-trip entirely.
      if (!isAdminLike) {
        if (!cancelled) { setAdmin(null); setAdminLoading(false); }
        return;
      }
      apiFetch("/auth/admin/me")
        .then(d => { if (!cancelled) setAdmin(d); })
        .catch(() => { if (!cancelled) setAdmin(null); })
        .finally(() => { if (!cancelled) setAdminLoading(false); });
    };

    runAuthBoot();
    return () => { cancelled = true; };
  }, [tryAutoLinkPlatform]);

  // Push the current customer id into the visitor analytics tracker so the
  // admin Live Visitors table can show the actual name instead of a hash.
  useEffect(() => {
    let cancelled = false;
    import("./analytics").then(m => {
      if (cancelled) return;
      const id = customer && (customer as any).id ? Number((customer as any).id) : null;
      m.setAnalyticsCustomerId(Number.isFinite(id as number) ? (id as number) : null);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [customer]);

  const applyMatch = async (c: any | null) => {
    try {
      const { setAdvancedMatch } = await import("./tracking");
      if (!c) { setAdvancedMatch(null); return; }
      setAdvancedMatch({
        email: c.email, phone: c.phone,
        firstName: c.firstName, lastName: c.lastName,
        city: c.city, country: c.country, zip: c.zip || c.postalCode,
        externalId: c.id ? String(c.id) : undefined,
      });
    } catch {}
  };

  const loginCustomer = async (email: string, password: string) => {
    const data = await apiFetch("/auth/customer/login", {
      method: "POST",
      body: JSON.stringify({ email, password }),
    });
    setCustomer(data);
    applyMatch(data);
    try { (await import("./tracking")).track({ eventName: "login" }); } catch {}
  };

  const registerCustomer = async (regData: { firstName: string; lastName: string; email: string; password: string; phone: string }) => {
    const data = await apiFetch("/auth/customer/register", {
      method: "POST",
      body: JSON.stringify(regData),
    });
    setCustomer(data);
    applyMatch(data);
    try { (await import("./tracking")).track({ eventName: "sign_up" }); } catch {}
  };

  const logoutCustomer = async () => {
    // Logging out of platform-auth also clears tenant session in one call.
    try {
      await apiFetch("/platform-auth/logout", { method: "POST" });
    } catch {
      await apiFetch("/auth/customer/logout", { method: "POST" });
    }
    setCustomer(null);
    applyMatch(null);
  };

  const loginAdmin = async (identifier: string, password: string) => {
    const data = await apiFetch("/auth/admin/login", {
      method: "POST",
      // Send both keys: `identifier` for the new multi-channel server, and
      // `username` for backwards-compat in case a stale server is reached.
      body: JSON.stringify({ identifier, username: identifier, password }),
    });
    setAdmin(data);
  };

  const logoutAdmin = async () => {
    await apiFetch("/auth/admin/logout", { method: "POST" });
    setAdmin(null);
  };

  return (
    <AuthContext.Provider value={{ customer, admin, customerLoading, adminLoading, loginCustomer, registerCustomer, logoutCustomer, loginAdmin, logoutAdmin, refreshCustomer }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
